import { createContext, useState, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { v7 as uuidv7 } from "uuid";
import {
  refetchFiles,
  useGetFilePath,
  useUpdateCaseFile,
} from "../files.query";
import { FileViewer } from "@monolith-forensics/monolith-ui";
import {
  File as CaseFile,
  FILE_SOURCE_TYPES,
  FileStatus,
  FileWithPath,
} from "../files.types";
import LoggingAPI from "@/api/logging";
import { usePermissions } from "@/hooks/usePermissions";
import { CaseFilesTableRef } from "../Tables";
import { getFoldersFromFiles, traverseFileTree } from "../utils";
import { FilesAPI } from "../files.api";
import { useFileUploader } from "./useFileUploader";
import { useStorageLimit } from "./useStorageLimit";
import type { FileUploaderFile } from "../Contexts/FileUploaderProvider";
import {
  EvidencePhotosAPI,
  applyUploadedEvidencePhoto,
} from "@/Features/EvidencePhotos";
import type { EvidencePhoto } from "@/Features/EvidencePhotos";
import {
  canDropInto,
  isManagedRow,
} from "@/Features/EvidenceFiles/evidence-files.utils";

// Add event types for the callback system
export type CaseFilesEvent =
  | { type: "file-clicked"; payload: { file: CaseFile } }
  | { type: "file-viewed"; payload: { file: CaseFile } }
  | {
      type: "files-added";
      payload: { fileNames: string[]; fileCount: number };
    }
  // Fires only after the storage-limit check passes; a rejected batch emits
  // "upload-rejected" instead
  | {
      type: "folder-uploaded";
      payload: { files: (File | FileWithPath)[]; parent?: CaseFile };
    }
  | { type: "files-refreshed"; payload: { folder_id?: string } }
  | { type: "search-changed"; payload: { search: string } }
  // Fires only after the storage-limit check passes; a rejected batch emits
  // "upload-rejected" instead
  | { type: "upload-started"; payload: { fileCount: number } }
  | { type: "upload-progress"; payload: { completed: number; total: number } }
  // The batch was refused by the storage-limit check and nothing was queued
  | {
      type: "upload-rejected";
      payload: { fileCount: number; message: string };
    };

export type CaseFilesEventCallback = (event: CaseFilesEvent) => void;

/** Which page the provider is on, for copy that names the other places. */
export type CaseFilesSurface = "case-files" | "evidence-files-tab";

export type CaseFilesFolder = {
  uuid: string;
  name: string;
  path_order: number;
  source_type?: string | null;
  source_id?: number | null;
};

export type CaseFilesContextType = {
  case_id: number;
  case_uuid: string;
  currentPath: string;
  currentPathArray: CaseFilesFolder[];
  // The folder being browsed, with its source fields (undefined at the case
  // root and while the path loads).
  currentFolder?: CaseFilesFolder;
  surface: CaseFilesSurface;
  folder_id?: string;
  handleFileClick: (file: CaseFile) => void;
  handleItemDrop: (e: React.DragEvent, parent?: CaseFile) => void;
  refetchFiles: () => void;
  setSearch: (search: string) => void;
  search: string;
  tableRef?: React.MutableRefObject<CaseFilesTableRef>;
  uploadFolder: (
    files: (File | FileWithPath)[],
    parent?: CaseFile
  ) => Promise<void>;
  uploadFiles: (files: File[]) => Promise<void>;
  // Add callback management methods
  addEventListener: (callback: CaseFilesEventCallback) => () => void;
  removeEventListener: (callback: CaseFilesEventCallback) => void;
};

export const CaseFilesContext = createContext<CaseFilesContextType>({
  case_id: 0,
  case_uuid: "",
  currentPath: "",
  currentPathArray: [],
  currentFolder: undefined,
  surface: "case-files",
  folder_id: undefined,
  handleFileClick: () => {},
  handleItemDrop: () => {},
  refetchFiles: () => {},
  setSearch: () => {},
  search: "",
  tableRef: undefined,
  uploadFolder: async () => {},
  uploadFiles: async () => {},
  addEventListener: () => () => {},
  removeEventListener: () => {},
});

/** Where an upload lands when the folder being browsed doesn't exist yet. */
export type CaseFilesUploadTarget = {
  source_type: string;
  source_id: number;
};

export const CaseFilesProvider: React.FC<{
  children: React.ReactNode;
  case_id: number;
  case_uuid: string;
  tableRef?: React.MutableRefObject<CaseFilesTableRef>;
  /**
   * The folder to browse when it isn't in the route. The Case Files page
   * reads `folder_id` from its URL; the evidence item Files tab (ENG-2156)
   * passes the item's folder, or nothing while the item has no folder yet.
   */
  folder_id?: string;
  /**
   * Upload routing for a folder that doesn't exist yet (`folder_id` unset):
   * an evidence item's first upload goes through the evidence photo routes,
   * which create its folder. Ignored once the folder is being browsed, since
   * its own source fields decide then.
   */
  uploadTarget?: CaseFilesUploadTarget;
  /**
   * The folder takes files only: a drop or folder upload that would create
   * subfolders is refused as a batch. Evidence item folders are flat.
   */
  flat?: boolean;
  surface?: CaseFilesSurface;
}> = ({
  children,
  case_id,
  case_uuid,
  tableRef,
  folder_id: folderIdProp,
  uploadTarget,
  flat = false,
  surface = "case-files",
}) => {
  const { folder_id: folderIdParam } = useParams();
  const folder_id = folderIdProp ?? folderIdParam;
  const navigate = useNavigate();
  const { currentUser } = usePermissions();
  const { addFiles } = useFileUploader();
  const queryClient = useQueryClient();
  const { checkStorageLimit } = useStorageLimit();
  const { enqueueSnackbar } = useSnackbar();
  const [showFileViewer, setShowFileViewer] = useState(false);
  const [selectedItem, setSelectedItem] = useState<CaseFile | null>(null);
  const [search, setSearch] = useState("");
  const { data, isLoading } = useGetFilePath({ uuid: folder_id || "root" });

  const { mutate: updateFile } = useUpdateCaseFile({
    onSuccess: () => {
      refetchFiles();
    },
  });

  // Add callback management
  const callbacksRef = useRef<Set<CaseFilesEventCallback>>(new Set());

  const currentPath = isLoading
    ? "Loading..."
    : data?.data?.map((item) => item.name).join("/") || "";

  const currentPathArray = isLoading ? [] : data?.data || [];

  // The folder being browsed, with its source fields (null at the root).
  const currentFolder = folder_id
    ? currentPathArray.find((item) => item.uuid === folder_id)
    : undefined;

  /**
   * Upload routes for a file landing directly in `target`. An evidence item
   * folder only takes uploads through the evidence photo routes (ENG-1445),
   * which tag the file to the item; everything else uses the `/files/*`
   * defaults. Internal row moves never come through here.
   */
  const uploadOptionsFor = (target?: {
    source_type?: string | null;
    source_id?: number | null;
  }): Pick<FileUploaderFile, "api" | "onUploaded"> => {
    if (
      target?.source_type !== FILE_SOURCE_TYPES.EVIDENCE_ITEM_FOLDER ||
      target.source_id == null
    ) {
      return {};
    }
    const evidenceId = target.source_id;
    return {
      api: EvidencePhotosAPI.uploaderApi(evidenceId),
      onUploaded: (result) => {
        const photo = (result as { data?: EvidencePhoto } | undefined)?.data;
        void applyUploadedEvidencePhoto(queryClient, evidenceId, photo);
      },
    };
  };

  // Where a file picked or dropped on the table itself lands: the folder
  // being browsed, or `uploadTarget` while that folder doesn't exist yet.
  const browsedTarget = currentFolder ?? uploadTarget;

  // Helper function to emit events
  const emitEvent = useCallback((event: CaseFilesEvent) => {
    callbacksRef.current.forEach((callback) => {
      try {
        callback(event);
      } catch (error) {
        console.error("Error in CaseFiles event callback:", error);
      }
    });
  }, []);

  // Add event listener
  const addEventListener = useCallback((callback: CaseFilesEventCallback) => {
    callbacksRef.current.add(callback);

    // Return cleanup function
    return () => {
      callbacksRef.current.delete(callback);
    };
  }, []);

  // Remove event listener
  const removeEventListener = useCallback(
    (callback: CaseFilesEventCallback) => {
      callbacksRef.current.delete(callback);
    },
    []
  );

  const handleFileClick = (file: CaseFile) => {
    // Emit file-clicked event
    emitEvent({ type: "file-clicked", payload: { file } });

    if (file.is_folder) {
      navigate(`/cases/${case_id}/documents/folders/${file.uuid}`);
    } else {
      setSelectedItem(file);
      setShowFileViewer(true);

      // Emit file-viewed event
      emitEvent({ type: "file-viewed", payload: { file } });

      LoggingAPI.createLog({
        case_id: case_id,
        user_id: currentUser.user_id,
        message: `Viewed File`,
        event: "case_file:view",
        object_type: "file",
        object_uuid: file.uuid,
      });
    }
  };

  const handleCloseFileViewer = () => {
    setSelectedItem(null);
    setShowFileViewer(false);
  };

  const uploadFolder = async (
    files: (File | FileWithPath)[],
    parent?: CaseFile
  ) => {
    // Determine Folders based on the file relative paths
    const parsedFolders = getFoldersFromFiles(Array.from(files));

    // A flat folder takes no subfolders, so refuse the batch before any row
    // is created rather than let the Files API reject each folder.
    if (flat && parsedFolders.length > 0) {
      const message =
        "Folders can't be uploaded here. Drop or pick files instead.";
      enqueueSnackbar(message, { variant: "error" });
      emitEvent({
        type: "upload-rejected",
        payload: { fileCount: files.length, message },
      });
      return;
    }

    // Create folder objects for API
    const folders = parsedFolders.map((folder) => ({
      uuid: folder.uuid,
      name: folder.name,
      is_folder: true,
      parent_uuid: folder.parent_uuid || parent?.uuid || folder_id || "root",
      case_uuid,
      path: `/${parent?.path || currentPath}/${folder.path}`,
    }));

    // Construct file objects for API
    const filesToUpload = Array.from(files).map((file) => {
      let itemPath = "";

      if ("file" in file) {
        itemPath = file.path;
      } else {
        itemPath = file.webkitRelativePath;
      }

      // Determine the parent folder for each file
      const parentFolder = parsedFolders.find((f) =>
        f.path.includes(itemPath.split("/").slice(0, -1).join("/"))
      );

      // Determine the parent path for each file
      const parentPath = folders.find(
        (f) => f.uuid === parentFolder?.uuid
      )?.path;

      let name = "";
      let size = 0;
      let ext = "";
      let client_modified = "";
      if ("file" in file) {
        name = file.file.name;
        size = file.file.size;
        ext = file.file.name.split(".").pop() || "";
        client_modified = new Date(file.file.lastModified).toISOString();
      } else {
        name = file.name;
        size = file.size;
        ext = file.name.split(".").pop() || "";
        client_modified = new Date(file.lastModified).toISOString();
      }

      // Construct the file object for API. A file inside a new subfolder goes
      // to that subfolder; the rest land in the drop target or current folder.
      const directTarget = parentFolder ? undefined : parent ?? browsedTarget;
      return {
        ...uploadOptionsFor(directTarget),
        file: "file" in file ? file.file : file,
        file_id: null as unknown as number,
        client_modified,
        uuid: uuidv7(),
        name,
        size,
        ext,
        is_folder: false,
        parent_uuid: parentFolder?.uuid || parent?.uuid || folder_id || "root",
        status: FileStatus.UPLOADING,
        case_uuid,
        path: `${parentPath || parent?.path || currentPath}/${name}`,
      };
    });

    // Reject the whole batch before any folder rows are created (ENG-1047)
    const storageCheck = await checkStorageLimit(filesToUpload);
    if (!storageCheck.allowed) {
      enqueueSnackbar(storageCheck.message, { variant: "error" });
      emitEvent({
        type: "upload-rejected",
        payload: {
          fileCount: filesToUpload.length,
          message: storageCheck.message,
        },
      });
      return;
    }

    // Emit folder-uploaded event
    emitEvent({ type: "folder-uploaded", payload: { files, parent } });

    // Create folders in the database
    FilesAPI.bulkCreate({
      files: folders,
    }).then((res) => {
      tableRef?.current?.refresh();
    });

    emitEvent({
      type: "files-added",
      payload: {
        fileNames: filesToUpload.map((f) => f.name),
        fileCount: filesToUpload.length,
      },
    });
    // Add files to the upload queue
    addFiles(filesToUpload);
  };

  const uploadFiles = async (files: File[]) => {
    const uploadOptions = uploadOptionsFor(browsedTarget);
    const fileObjects = files.map((file) => ({
      ...uploadOptions,
      file,
      file_id: null as unknown as number,
      client_modified: new Date(file.lastModified).toISOString(),
      uuid: uuidv7(),
      name: file.name,
      size: file.size,
      ext: file.name.split(".").pop() || "",
      is_folder: false,
      parent_uuid: folder_id || "root",
      status: FileStatus.UPLOADING,
      case_uuid,
      path: `/${currentPath}/${file.name}`,
    }));

    // Reject the whole batch before anything is queued (ENG-1047)
    const storageCheck = await checkStorageLimit(fileObjects);
    if (!storageCheck.allowed) {
      enqueueSnackbar(storageCheck.message, { variant: "error" });
      emitEvent({
        type: "upload-rejected",
        payload: { fileCount: files.length, message: storageCheck.message },
      });
      return;
    }

    // Emit upload-started event
    emitEvent({ type: "upload-started", payload: { fileCount: files.length } });

    // Emit files-uploaded event
    emitEvent({
      type: "files-added",
      payload: { fileNames: files.map((f) => f.name), fileCount: files.length },
    });

    addFiles(fileObjects);
  };

  const handleItemDrop = async (e: React.DragEvent, parent?: CaseFile) => {
    e.preventDefault();
    e.stopPropagation(); // prevent the drop from being handled by the table

    const internalDrop = e.dataTransfer.getData("text/plain");

    if (internalDrop) {
      // Handle internal file/folder drops
      try {
        const droppedData = JSON.parse(
          e.dataTransfer.getData("text/plain")
        ) as CaseFile;
        // Nothing moves into, out of, or around the managed Evidence Files
        // tree (ENG-2133); the Files API answers 403 anyway. A drop on the
        // table plane lands in the folder being browsed.
        if (
          isManagedRow(droppedData) ||
          !canDropInto(parent ?? currentFolder, { internal: true })
        ) {
          return;
        }
        // Here you would typically call an API to move the file/folder
        updateFile({
          uuid: droppedData.uuid,
          parent_uuid: parent?.uuid,
        });
      } catch (error) {
        console.error("Error handling drop:", error);
      }
    } else {
      // Handle external files/folders from file system
      if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
        const items = Array.from(e.dataTransfer.items);
        const allFiles: FileWithPath[] = [];

        // First, try to process items that have valid entries (usually the first one)
        const validEntries: {
          item: DataTransferItem;
          entry: FileSystemEntry;
        }[] = [];
        const failedItems: DataTransferItem[] = [];

        for (const item of items) {
          // Don't rely on item.kind as it's unreliable with multiple items
          // Instead, try to get the entry and let that determine the type
          const entry = item.webkitGetAsEntry();

          if (entry) {
            validEntries.push({ item, entry });
          } else {
            failedItems.push(item);
          }
        }

        // Process valid entries (these can include folders)
        for (const { entry } of validEntries) {
          try {
            const files = await traverseFileTree(entry);
            allFiles.push(...files);
          } catch (error) {
            console.error("Error processing entry:", entry.name, error);
          }
        }

        // Handle failed items by falling back to direct file access
        // Note: This won't preserve folder structure for folders, but will get the files
        for (const item of failedItems) {
          if (item.kind === "file") {
            const file = item.getAsFile();
            if (file) {
              allFiles.push({
                file,
                path: file.name,
              });
            }
          }
        }

        // Additional fallback: use e.dataTransfer.files if we still have no files
        // This handles cases where the items API completely fails
        if (
          allFiles.length === 0 &&
          e.dataTransfer.files &&
          e.dataTransfer.files.length > 0
        ) {
          const filesFromFallback: FileWithPath[] = Array.from(
            e.dataTransfer.files
          ).map((file) => ({
            file,
            path: file.name,
          }));
          allFiles.push(...filesFromFallback);
        }

        if (allFiles.length > 0) {
          // update files paths to include the folder name
          allFiles.forEach((file) => {
            file.path = file.path.startsWith("/")
              ? file.path.slice(1)
              : file.path;
          });
          await uploadFolder(allFiles, parent);
        }
        return;
      }

      // TODO: handle this edge case
      // Fallback for simple file drops (when items API is not available)
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        const filesWithPath: FileWithPath[] = Array.from(
          e.dataTransfer.files
        ).map((file) => ({
          file,
          path: file.name, // Simple file drops don't have folder structure
        }));
        return;
      }
    }
  };

  const handleRefetchFiles = useCallback(() => {
    refetchFiles({ parent_uuid: folder_id || "root" });
    // Emit files-refreshed event
    emitEvent({ type: "files-refreshed", payload: { folder_id } });
  }, [folder_id, emitEvent]);

  const handleSetSearch = useCallback(
    (newSearch: string) => {
      setSearch(newSearch);
      // Emit search-changed event
      emitEvent({ type: "search-changed", payload: { search: newSearch } });
    },
    [emitEvent]
  );

  return (
    <CaseFilesContext.Provider
      value={{
        case_id,
        case_uuid,
        currentPath,
        currentPathArray,
        currentFolder,
        surface,
        folder_id,
        handleFileClick,
        handleItemDrop,
        refetchFiles: handleRefetchFiles,
        setSearch: handleSetSearch,
        search,
        tableRef,
        uploadFolder,
        uploadFiles,
        addEventListener,
        removeEventListener,
      }}
    >
      {children}
      <FileViewer
        file={{
          ext: selectedItem?.name?.split(".").pop(),
          url: selectedItem?.link,
          name: selectedItem?.name || "",
          size: selectedItem?.size,
          type: selectedItem?.ext,
        }}
        open={showFileViewer}
        onClose={handleCloseFileViewer}
      />
    </CaseFilesContext.Provider>
  );
};
